import { Link } from 'react-router-dom';
import {
  Loader2,
  DollarSign,
  Clock,
  Database,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react';
import { useRunsStore } from '@/stores/runs';
import type { ResearchRunSummary } from '@/services/api';

function formatDuration(seconds?: number | null): string {
  if (!seconds) return '—';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
}) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={12} className="text-slate-400" />
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div className="text-lg font-semibold text-navy-900">{value}</div>
    </div>
  );
}

export function RunDetail() {
  const { selectedRunSummary, selectedRunDetail, detailLoading } = useRunsStore();

  if (detailLoading && !selectedRunDetail) {
    return (
      <div className="h-full flex items-center justify-center text-slate-400">
        <Loader2 size={16} className="animate-spin mr-2" />
        Loading run...
      </div>
    );
  }

  if (!selectedRunSummary) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-slate-400">
        Select a research run to view detail
      </div>
    );
  }

  const s = selectedRunSummary;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-navy-900">
              {s.research_type
                .split('_')
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(' ')}
            </h2>
            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
              <code>{s.id}</code>
              <span className="uppercase font-medium">{s.status}</span>
              {s.client_id && <span>client: {s.client_id}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
        {/* Stat grid */}
        <div className="grid grid-cols-4 gap-3">
          <StatCard
            icon={Database}
            label="Entities Created"
            value={s.entities_created_count}
          />
          <StatCard
            icon={Database}
            label="Entities Updated"
            value={s.entities_updated_count}
          />
          <StatCard icon={Clock} label="Duration" value={formatDuration(s.duration_seconds)} />
          <StatCard
            icon={DollarSign}
            label="Cost"
            value={`$${s.cost_estimate.toFixed(2)}`}
          />
        </div>

        {/* Progress for running */}
        {(s.status === 'in_progress' || s.status === 'running') && (
          <div className="border border-slate-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-slate-700">
                {s.current_phase ?? 'Running'}
              </span>
              <span className="text-xs text-slate-500">{s.progress}%</span>
            </div>
            <div className="bg-slate-100 rounded-full h-1.5 overflow-hidden">
              <div
                className="h-full rounded-full bg-brand-teal transition-all duration-500"
                style={{ width: `${s.progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Error message */}
        {s.error_message && (
          <div className="flex items-start gap-2 p-3 rounded-lg border border-rose-200 bg-rose-50">
            <AlertTriangle size={14} className="text-rose-500 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-rose-700">{s.error_message}</div>
          </div>
        )}

        {/* Secondary metrics */}
        <SummaryStrip summary={s} />

        {/* Entities drill-through */}
        <div>
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
            Outputs
          </h3>
          <div className="grid grid-cols-2 gap-2">
            <Link
              to={`/knowledge/estate/concepts?research_id=${s.id}`}
              className="flex items-center justify-between px-3 py-2 rounded-lg border border-slate-200 hover:border-brand-teal/50 hover:bg-brand-teal/5 transition-colors"
            >
              <span className="text-sm text-slate-700">View raw concepts</span>
              <ArrowRight size={14} className="text-slate-400" />
            </Link>
            {selectedRunDetail?.follow_up_tasks &&
              selectedRunDetail.follow_up_tasks.length > 0 && (
                <div className="px-3 py-2 rounded-lg border border-slate-200 bg-slate-50">
                  <div className="text-xs text-slate-500 uppercase font-medium">
                    Follow-up Tasks
                  </div>
                  <div className="text-sm text-slate-700 mt-0.5">
                    {selectedRunDetail.follow_up_tasks.length}
                  </div>
                </div>
              )}
          </div>
        </div>

        {/* Knowledge gaps */}
        {selectedRunDetail?.knowledge_gaps &&
          selectedRunDetail.knowledge_gaps.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                Knowledge Gaps ({selectedRunDetail.knowledge_gaps.length})
              </h3>
              <div className="space-y-1.5">
                {selectedRunDetail.knowledge_gaps.slice(0, 10).map((g, i) => (
                  <div
                    key={i}
                    className="text-xs border border-slate-200 rounded-lg px-3 py-2 bg-slate-50"
                  >
                    <pre className="text-slate-700 whitespace-pre-wrap">
                      {JSON.stringify(g, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            </div>
          )}
      </div>
    </div>
  );
}

function SummaryStrip({ summary: s }: { summary: ResearchRunSummary }) {
  const items: Array<{ label: string; value: string | number }> = [
    { label: 'Searches', value: s.total_searches },
    { label: 'Processed', value: s.total_entities_processed },
    { label: 'Tokens', value: s.tokens_used.toLocaleString() },
    { label: 'Follow-ups', value: s.follow_up_task_count },
    { label: 'Gaps', value: s.knowledge_gap_count },
  ];

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
      <div className="flex items-center justify-between gap-4 text-xs">
        {items.map((item) => (
          <div key={item.label}>
            <div className="text-[10px] text-slate-500 uppercase font-medium">
              {item.label}
            </div>
            <div className="text-sm font-semibold text-navy-900">{item.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
